import { logger } from '@/helpers/log.service';
import { arrayBufferToBase64 } from '@/helpers/utility';
import { IDeviceReadedKeyInfo, IKeyMedia } from '@/models/eusign-native.models';
import { BaseMobileService } from './base-mobile.service';

class EuSignNativeService extends BaseMobileService {
  constructor() {
    super('plugins.euSignPlugin', 'EuSignNativeService');
  }

  protected execute(fn: string, ...args: any): Promise<any> {
    const msg = `[${this.serviceName}:${fn}]`;
    logger.debug(`${msg} executing with args:`, ...args);

    return new Promise(async(resolve, reject) => {
      try {
        this.demandServiceExists();

        this.demandServiceMethodExists(fn);

        const result = await this.getServiceInstance()[fn](...args);

        this.handleResult(msg, result, resolve);
      } catch (error) {
        this.handleError(msg, error, reject);
      }
    });
  }

  public findDevices(): Promise<Array<any>> {
    return this.execute('findDevices') as Promise<Array<any>>;
  }

  public readKey(keyMedia: IKeyMedia): Promise<any> {
    return this.execute('readKey', keyMedia) as Promise<any>;
  }

  public resetPrivateKey(): Promise<any> {
    return this.execute('resetPrivateKey') as Promise<any>;
  }

  public isPrivateKeyReaded(): Promise<boolean> {
    return this.execute('isPrivateKeyReaded') as Promise<boolean>;
  }

  public signHash(hash: string, readedKeyInfo?: IDeviceReadedKeyInfo): Promise<string> {
    let prms = { hash } as any;
    if (readedKeyInfo) {
      prms = { ...prms, ...readedKeyInfo };
    }
    return this.execute('signHash', prms).then(res => res.sign) as Promise<string>;
  }

  public signData(data: Uint8Array | string, readedKeyInfo?: IDeviceReadedKeyInfo): Promise<string> {
    let prms = {} as any;
    if (typeof data === 'string') {
      prms.data = data;
    } else {
      prms.binaryData = arrayBufferToBase64(data);
    }
    if (readedKeyInfo) {
      prms = { ...prms, ...readedKeyInfo };
    }
    return this.execute('signData', prms).then(res => res.sign) as Promise<string>;
  }

  public getCertificate(): Promise<Array<any>> {
    return this.execute('getCertificate') as Promise<Array<any>>;
  }
}

export default new EuSignNativeService();
